import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import ConfirmDestructiveDialog from '@/components/ConfirmDestructiveDialog';
import { ErrorBanner } from '@/components/ui';
import { templatesService, type SearchTemplate } from '@/api/services/templates';
import { useMe } from '@/context/MeContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useToast } from '@/components/Toast';
import { formatApiError } from '@/utils/formatApiError';
import {
  canExportTemplates,
  canImportTemplates,
  canInstallTemplates,
  canManageOrganizationTemplates,
  canManagePersonalTemplates,
} from '@/utils/navCapabilities';

function downloadTemplateJson(title: string, data: Record<string, unknown>) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${title.replace(/\s+/g, '-').toLowerCase() || 'template'}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function TemplateMyPage() {
  const { me } = useMe();
  const { isPersonalMode, activeWorkspaceName } = useWorkspace();
  const { toast } = useToast();
  const location = useLocation();
  const importInputRef = useRef<HTMLInputElement>(null);
  const canManageOrg = canManageOrganizationTemplates(me);
  const canManagePersonal = canManagePersonalTemplates(me);
  const canImport = isPersonalMode
    ? canImportTemplates(me) && canInstallTemplates(me)
    : canManageOrg;
  const canEditTemplates = isPersonalMode ? canManagePersonal : canManageOrg;
  const canExport = canExportTemplates(me);
  const [items, setItems] = useState<SearchTemplate[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [archiveTarget, setArchiveTarget] = useState<SearchTemplate | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await templatesService.myList();
      setItems(r.items);
    } catch (e) {
      setError(formatApiError(e, isPersonalMode ? 'Failed to load personal templates' : 'Failed to load organization templates'));
    } finally {
      setLoading(false);
    }
  }, [isPersonalMode]);

  useEffect(() => {
    void load();
  }, [load, location.key, location.state]);

  async function publish(id: string) {
    setBusyId(id);
    try {
      await templatesService.orgPublish(id);
      toast('Template published', 'success');
      await load();
    } catch (e) {
      setError(formatApiError(e, 'Publish failed'));
    } finally {
      setBusyId(null);
    }
  }

  async function exportTemplate(template: SearchTemplate) {
    setBusyId(template.id);
    try {
      const data = await templatesService.export(template.id);
      downloadTemplateJson(template.title, data);
      toast('Template exported', 'success');
    } catch (e) {
      setError(formatApiError(e, 'Export failed'));
    } finally {
      setBusyId(null);
    }
  }

  async function confirmArchive() {
    if (!archiveTarget) return;
    setBusyId(archiveTarget.id);
    setError('');
    try {
      await templatesService.orgArchive(archiveTarget.id);
      toast('Template archived', 'success');
      setArchiveTarget(null);
      await load();
    } catch (e) {
      setError(formatApiError(e, 'Archive failed'));
    } finally {
      setBusyId(null);
    }
  }

  async function handleImportFile(file: File) {
    setError('');
    try {
      const text = await file.text();
      const payload = JSON.parse(text) as Record<string, unknown>;
      await templatesService.import(payload);
      toast('Template imported', 'success');
      await load();
    } catch (e) {
      setError(formatApiError(e, 'Import failed — check that the JSON export is valid'));
    }
  }

  return (
    <>
      <PageHeader
        title={isPersonalMode ? 'Personal templates' : 'Organization templates'}
        subtitle={
          isPersonalMode
            ? 'Templates you created in your Personal workspace'
            : `Reusable templates shared with ${activeWorkspaceName ?? 'your organization'}`
        }
      >
        <Link to="/templates" className="btn btn-ghost btn-sm">
          Marketplace
        </Link>
        {canImport ? (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => importInputRef.current?.click()}
            >
              Import JSON
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImportFile(file);
                e.target.value = '';
              }}
            />
          </>
        ) : null}
      </PageHeader>
      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}
      {loading ? <p className="muted">Loading…</p> : null}
      {!loading && items.length === 0 ? (
        <p className="muted">
          {isPersonalMode ? 'No personal templates yet.' : 'No organization templates yet.'}
        </p>
      ) : null}
      <div className="template-grid">
        {items.map((t) => (
          <article key={t.id} className="card card-elevated template-card">
            <h3>{t.title}</h3>
            <p className="muted">{t.shortDescription}</p>
            <p className="muted">Status: {t.status}</p>
            <div className="template-card__actions">
              <Link to={`/templates/${t.id}`} className="btn btn-sm btn-ghost">
                Preview
              </Link>
              {canExport ? (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={busyId === t.id}
                  onClick={() => void exportTemplate(t)}
                >
                  Export
                </button>
              ) : null}
              {canEditTemplates && t.status === 'draft' ? (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busyId === t.id}
                  onClick={() => void publish(t.id)}
                >
                  Publish
                </button>
              ) : null}
              {canEditTemplates && t.status !== 'archived' ? (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost text-danger"
                  disabled={busyId === t.id}
                  onClick={() => setArchiveTarget(t)}
                >
                  Archive
                </button>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      {archiveTarget ? (
        <ConfirmDestructiveDialog
          title="Archive template?"
          titleId="archive-template-title"
          onClose={() => setArchiveTarget(null)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setArchiveTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={busyId === archiveTarget.id}
                onClick={() => void confirmArchive()}
              >
                Archive template
              </button>
            </>
          }
        >
          <p>
            <strong>{archiveTarget.title}</strong> will be hidden from My Templates.
          </p>
          <p className="muted">Installed searches will not be affected.</p>
        </ConfirmDestructiveDialog>
      ) : null}
    </>
  );
}
